import { useRelevantQueryParams } from '@frontend/common';
import { useModals } from '@frontend/modal';
import { ReportClient } from '@frontend/report/api';
import { Report, ReportType } from '@frontend/report/types';
import { useAccountRepository, useLicenseRepository } from '@frontend/repository';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import { useAppDispatch } from '../../../redux/store';
import { useIsMount } from '../../../utils/hooks';
import { getPudoTransactionStatesForTab } from '../pudo-transaction-tab-states';

export const useTransactionsCollis = () => {
    const { openModal } = useModals();
    const { currentAccount } = useAccountRepository();
    const { functionalities } = useLicenseRepository();
    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as { functionalityId?: string } | null;
    const formId = 'pudo-transaction-collis-form';
    const [trackingNumberValid, setTrackingNumberValid] = useState<boolean>(true);
    const [activeTab, setActiveTab] = useState<string>('active');
    const dispatch = useAppDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const isMount = useIsMount();

    const [report, setReport] = useState<Report | null>(null);
    const reportModalId = 'pudo-transactions-collis-report-modal';
    const data: { [key: string]: any } = { ...useRelevantQueryParams(['tab']) };
    const functionalityId = locationState?.functionalityId ?? searchParams.get('functionalityId');
    const functionality = useMemo(() => {
        if (!functionalityId) return null;
        return functionalities.find((f) => f.id === functionalityId) ?? null;
    }, [functionalities, functionalityId]);

    useEffect(() => {
        ReportClient.fetchReports({ type: ReportType.PUDO_TRANSACTIONS, account_id: currentAccount }).then((res) => {
            if (res.results.length > 0) {
                setReport(res.results[0]);
            }
        });
    }, [currentAccount]);

    const functionalityWizardId = functionality?.wizard_id ?? null;

    useEffect(() => {
        if (isMount) {
            return;
        } else {
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    params.set('tab', activeTab);
                    return params;
                },
                { state: locationState ?? undefined }
            );
        }
    }, [activeTab]);

    const exportData = useMemo(() => {
        const state = getPudoTransactionStatesForTab(activeTab);
        if (state) {
            data['state'] = state;
        }
        return data;
    }, [searchParams, activeTab]);

    return {
        openModal,
        currentAccount,
        navigate,
        location,
        formId,
        trackingNumberValid,
        setTrackingNumberValid,
        activeTab,
        setActiveTab,
        dispatch,
        report,
        reportModalId,
        exportData,
        functionality,
        functionalityWizardId
    };
};
